import React, { useState, useEffect } from 'react';
import { auth, signInWithGoogle } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

export default function AdminApp() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [licenses, setLicenses] = useState([]);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                fetchAdminData(currentUser);
            } else {
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const fetchAdminData = async (currentUser) => {
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch('/api/admin/data', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                if (res.status === 403) throw new Error("You do not have administrative privileges.");
                throw new Error("Failed to connect to admin database.");
            }

            const data = await res.json();
            setUsers(data.users);
            setLicenses(data.licenses);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignLicense = async (userId, licenseId) => {
        try {
            const token = await auth.currentUser.getIdToken();
            const res = await fetch('/api/admin/assign', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ userId, licenseId })
            });

            if (!res.ok) throw new Error("Failed to assign license.");

            // Update UI instantly
            setUsers(users.map(u => u.id === userId ? { ...u, licenseId: licenseId === "none" ? null : licenseId } : u));
            setSuccessMessage("License assignment saved!");
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (e) {
            alert(e.message);
        }
    };

    if (loading) return <div className="bouncing-dots" style={{ margin: '100px auto' }}><div/><div/><div/></div>;

    if (!user) {
        return (
            <div className="disc-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <div style={{ textAlign: 'center', padding: '40px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border-subtle)', maxWidth: '400px', width: '90%' }}>
                    <h2>Admin Portal</h2>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>Please sign in to access the control panel.</p>
                    <button onClick={signInWithGoogle} className="share-btn" style={{ marginTop: 0 }}>Sign in with Google</button>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="disc-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <div style={{ textAlign: 'center', padding: '40px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid #ef4444', maxWidth: '400px', width: '90%' }}>
                    <div style={{ fontSize: '40px', marginBottom: '10px' }}>🛑</div>
                    <h2 style={{ color: '#ef4444' }}>Access Denied</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="disc-wrapper">
            <header className="slim-header" style={{ padding: '15px 30px' }}>
                <div className="header-title" style={{ margin: 0 }}>Onyx Command Center</div>
                <button className="header-reset" onClick={() => window.location.href = '/'}>Exit</button>
            </header>

            <div className="app-layout" style={{ paddingTop: '80px', maxWidth: '1200px', margin: '0 auto', paddingBottom: '60px' }}>
                
                {/* Dashboard Header */}
                <div className="admin-header">
                    <div>
                        <h2 style={{ margin: '0 0 5px 0' }}>User License Management</h2>
                        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px' }}>
                            Total Users: <strong>{users.length}</strong> | Active Licenses: <strong>{licenses.length}</strong>
                        </p>
                    </div>
                    {successMessage && <div className="admin-success-toast animate-slide-up">{successMessage}</div>}
                </div>

                {/* Mobile-Friendly Card List */}
                <div className="admin-user-list">
                    {users.map(u => (
                        <div key={u.id} className="admin-user-card">
                            
                            <div className="admin-user-info">
                                <strong>{u.profile?.name || 'Unknown User'}</strong>
                                <span>{u.profile?.email}</span>
                            </div>
                            
                            <div className="admin-user-status">
                                {u.licenseId ? (
                                    <span className="badge premium">Key: {u.licenseId}</span>
                                ) : (
                                    <span className="badge free">Free Tier</span>
                                )}
                            </div>
                            
                            <div className="admin-user-action">
                                <select 
                                    className="admin-select"
                                    value={u.licenseId || "none"}
                                    onChange={(e) => handleAssignLicense(u.id, e.target.value)}
                                >
                                    <option value="none">-- Revoke / Set to Free --</option>
                                    {licenses.map(lic => (
                                        <option key={lic.id} value={lic.id}>
                                            {lic.company || lic.organisation || "Unnamed License"} ({lic.id})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ))}

                    {users.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                            No users found in the database.
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}